import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface TeamMember {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  teamRole: string;
  createdAt: string;
  lastLoginAt: string | null;
}

interface Invitation {
  id: number;
  email: string;
  role: string;
  invitationToken: string;
  expiresAt: string;
  createdAt: string;
  invitedByFirstName: string;
  invitedByLastName: string;
}

interface ExportLog {
  id: number;
  project_name: string;
  export_type: string;
  row_count: number;
  file_name: string;
  created_at: string;
}

const TeamManagement: React.FC = () => {
  const { user, team, createTeam, token } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Member and invitation management state
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Export activity state
  const [recentExports, setRecentExports] = useState<ExportLog[]>([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);

  const API_BASE = '/api';

  // Fetch members and invitations when team is loaded
  useEffect(() => {
    if (user?.teamId) {
      loadMembers();
      loadRecentExports();
      if (user.teamRole === 'admin') {
        loadInvitations();
      }
    }
  }, [user?.teamId, user?.teamRole]);

  const loadMembers = async () => {
    if (!user?.teamId) return;

    try {
      setIsLoadingMembers(true);
      const response = await fetch(`${API_BASE}/teams/${user.teamId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const loadInvitations = async () => {
    if (!user?.teamId) return;

    try {
      setIsLoadingInvitations(true);
      const response = await fetch(`${API_BASE}/teams/${user.teamId}/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const loadRecentExports = async () => {
    if (!user?.teamId || !user?.id) return;

    try {
      setIsLoadingExports(true);
      // Fetch recent exports for this user (limit to 10)
      const response = await fetch(
        `${API_BASE}/teams/${user.teamId}/export-logs?limit=10&userId=${user.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRecentExports(data.exports || []);
      }
    } catch (error) {
      console.error('Failed to load recent exports:', error);
    } finally {
      setIsLoadingExports(false);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      setIsCreating(true);
      setError('');
      await createTeam(teamName.trim());
      setShowCreateModal(false);
      setTeamName('');
    } catch (err: any) {
      setError(err.message || 'Failed to create team');
    } finally {
      setIsCreating(false);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    try {
      setIsInviting(true);
      setInviteError('');
      const response = await fetch(`${API_BASE}/teams/${user?.teamId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create invitation');
      }

      const data = await response.json();

      // Show invitation link (as fallback)
      const link = `${window.location.origin}/invitation/${data.invitation.invitationToken}`;
      setInvitationLink(link);
      setEmailSent(data.emailSent || false);

      // Reset form but keep modal open to show confirmation
      setInviteEmail('');
      setInviteRole('member');

      // Reload invitations list
      loadInvitations();
    } catch (err: any) {
      setInviteError(err.message || 'Failed to invite member');
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: number) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams/invitation/${invitationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invitation');
      }

      // Reload invitations
      loadInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      alert('Failed to cancel invitation');
    }
  };

  const handleChangeRole = async (userId: number, newRole: string, memberName: string) => {
    if (!window.confirm(`Change ${memberName}'s role to ${newRole}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams/${user?.teamId}/members/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error('Failed to update role');
      }

      // Reload members
      loadMembers();
    } catch (error) {
      console.error('Failed to change role:', error);
      alert('Failed to change member role');
    }
  };

  const handleRemoveMember = async (userId: number, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from the team? Their projects will no longer be shared with the team.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams/${user?.teamId}/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to remove member');
      }

      // Reload members
      loadMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('Failed to remove member');
    }
  };

  const handleResendInvitation = async (invitationId: number) => {
    try {
      const response = await fetch(`${API_BASE}/teams/invitation/${invitationId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to resend invitation');
      }

      const data = await response.json();
      if (data.emailSent) {
        alert('Invitation email resent successfully!');
      } else {
        alert('Failed to send email. You can still share the invitation link manually.');
      }

      // Reload invitations to show updated expiry
      loadInvitations();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      alert('Failed to resend invitation');
    }
  };

  const copyInvitationLink = () => {
    navigator.clipboard.writeText(invitationLink);
    alert('Invitation link copied to clipboard!');
  };

  // If user doesn't have a team, show create team UI
  if (!user?.teamId) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 text-center animate-fade-in">
          <div className="text-6xl mb-4">👥</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
            Create Your Team
          </h2>
          <p className="text-gray-600 mb-6">
            You don't have a team yet. Create one to collaborate with your colleagues and manage access to your projects.
          </p>

          {!showCreateModal ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary px-6 py-3"
            >
              Create Team
            </button>
          ) : (
            <form onSubmit={handleCreateTeam} className="max-w-md mx-auto mt-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Team Name
                </label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., Acme Corp, Sales Team"
                  className="input"
                  autoFocus
                  disabled={isCreating}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setTeamName('');
                    setError('');
                  }}
                  className="btn-secondary px-6 py-2"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="btn-primary px-6 py-2"
                >
                  {isCreating ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200/80 rounded-xl p-6 animate-fade-in">
          <h3 className="font-semibold text-blue-900 mb-2 tracking-tight">What is Team Management?</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Invite colleagues to collaborate on your projects</li>
            <li>• Control who can view, edit, or export data</li>
            <li>• Track team activity and exports</li>
            <li>• Share credits across your team</li>
          </ul>
        </div>
      </div>
    );
  }

  // User has a team - show team management interface
  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 tracking-tight">
          {team?.name || 'Team Management'}
        </h2>
        <p className="text-gray-600">
          Your role: <span className="font-medium capitalize">{user.teamRole}</span>
        </p>
      </div>

      {/* Team Members section */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Team Members</h3>
          {user.teamRole === 'admin' && (
            <button
              onClick={() => {
                setShowInviteForm(true);
                setInvitationLink('');
                setEmailSent(false);
                setInviteError('');
              }}
              className="btn-primary text-sm"
            >
              Invite Member
            </button>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteForm && (
          <div className="modal-backdrop">
            <div className="modal-dialog p-6 max-w-lg w-full mx-4 animate-scale-in">
              <h3 className="text-lg font-semibold mb-4 tracking-tight">Invite Team Member</h3>

              {!invitationLink ? (
                <form onSubmit={handleInviteMember}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="colleague@company.com"
                      className="input"
                      autoFocus
                      disabled={isInviting}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="input"
                      disabled={isInviting}
                    >
                      <option value="admin">Admin - Full access, can manage team</option>
                      <option value="member">Member - Can view and export data</option>
                      <option value="viewer">Viewer - Read-only access</option>
                    </select>
                  </div>

                  {inviteError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {inviteError}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteForm(false);
                        setInviteEmail('');
                        setInviteRole('member');
                        setInviteError('');
                      }}
                      className="btn-secondary"
                      disabled={isInviting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isInviting}
                      className="btn-primary"
                    >
                      {isInviting ? 'Sending...' : 'Send Invitation'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    {emailSent
                      ? 'Invitation email sent! The link below is also available as a backup.'
                      : 'Invitation created! Share the sign-up link below with your team member.'}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sign-up Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={invitationLink}
                        readOnly
                        className="flex-1 input bg-gray-50 text-sm"
                      />
                      <button
                        onClick={copyInvitationLink}
                        className="btn-secondary text-sm"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      This link expires in 7 days.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowInviteForm(false);
                        setInvitationLink('');
                        setEmailSent(false);
                        setInviteEmail('');
                        setInviteRole('member');
                      }}
                      className="btn-primary"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isLoadingMembers ? (
          <div className="text-center py-8 text-gray-500">Loading members...</div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200/80 rounded-xl transition-all duration-200 hover:shadow-card">
                <div>
                  <div className="font-medium text-gray-900">
                    {member.firstName} {member.lastName}
                  </div>
                  <div className="text-sm text-gray-500">{member.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    member.teamRole === 'admin' ? 'bg-purple-100 text-purple-700' :
                    member.teamRole === 'member' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {member.teamRole}
                  </span>
                  {user.teamRole === 'admin' && member.id !== user.id && (
                    <div className="flex gap-2">
                      <select
                        value={member.teamRole}
                        onChange={(e) => handleChangeRole(member.id, e.target.value, `${member.firstName} ${member.lastName}`)}
                        className="text-sm border border-gray-200/80 rounded-lg px-2 py-1 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        onClick={() => handleRemoveMember(member.id, `${member.firstName} ${member.lastName}`)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations section - only visible to admins */}
      {user.teamRole === 'admin' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 tracking-tight">Pending Invitations</h3>

          {isLoadingInvitations ? (
            <div className="text-center py-8 text-gray-500">Loading invitations...</div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No pending invitations</div>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-4 border border-gray-200/80 rounded-xl transition-all duration-200 hover:shadow-card">
                  <div>
                    <div className="font-medium text-gray-900">{invitation.email}</div>
                    <div className="text-sm text-gray-500">
                      Role: {invitation.role} • Invited {new Date(invitation.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => {
                        const link = `${window.location.origin}/invitation/${invitation.invitationToken}`;
                        navigator.clipboard.writeText(link);
                        alert('Invitation link copied to clipboard!');
                      }}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Export Activity section */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 tracking-tight">Your Recent Exports</h3>

        {isLoadingExports ? (
          <p className="text-gray-600 text-center py-8">Loading exports...</p>
        ) : recentExports.length === 0 ? (
          <p className="text-gray-600 text-center py-8">
            No exports yet. Export data from any project to see your activity here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rows</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentExports.map((exportLog) => (
                  <tr key={exportLog.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{exportLog.project_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {exportLog.export_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{exportLog.row_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{exportLog.file_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(exportLog.created_at).toLocaleDateString()} {new Date(exportLog.created_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {user?.teamRole === 'admin' && (
          <div className="mt-4 text-sm text-gray-600">
            💡 As an admin, you can view all team exports in the <strong>Audit Logs</strong> tab.
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
